import { DefaultLoader, LoaderCoverContainer } from 'components/Loader';
import MentorStats from 'components/MentorStats';
import ProfileNavigation from 'components/profileNavigation';
import ProfileLink from 'components/profileNavigation/Link';
import Space from 'components/Space';
import React, { FC, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import User from 'types/User';
import ChangeReferee from './ChangeReferee';
import MentorProfileRoutes from './Routes';

type RetrievedData = {
  wantedMentorship: number | undefined;
  assignedMentorship: number | undefined;
  finishedMentorship: number | undefined;
  interruptedMentorship: number | undefined;
  lateMentorship: number | undefined;
};

type ContextType = {
  mentor: User | undefined;
  wantedMentorship: number | undefined;
  assignedMentorship: number | undefined;
  finishedMentorship: number | undefined;
  interruptedMentorship: number | undefined;
  lateMentorship: number | undefined;
};
export const MentorEditContext = React.createContext({} as ContextType);
const { Provider } = MentorEditContext;

const MentorEdit: FC = ({ children }) => {
  const { id } = useParams();

  const mentor: User | undefined = useSWR(`/user/${id}`).data;

  const { data, error } = useSWR<RetrievedData>(
    `/mentorship/mentor/${id}/stats`,
  );

  const {
    wantedMentorship,
    assignedMentorship,
    finishedMentorship,
    interruptedMentorship,
    lateMentorship,
  } = useMemo(
    () =>
      data || {
        wantedMentorship: undefined,
        assignedMentorship: undefined,
        finishedMentorship: undefined,
        interruptedMentorship: undefined,
        lateMentorship: undefined,
      },
    [data],
  );

  const value = useMemo(
    () => ({
      mentor,
      wantedMentorship,
      assignedMentorship,
      finishedMentorship,
      interruptedMentorship,
      lateMentorship,
    }),
    [
      mentor,
      wantedMentorship,
      assignedMentorship,
      finishedMentorship,
      interruptedMentorship,
      lateMentorship,
    ],
  );

  return error ? (
    <p>Une erreur est survenue.</p>
  ) : (
    <div style={{ position: 'relative' }}>
      {!data && (
        <LoaderCoverContainer>
          <DefaultLoader />
        </LoaderCoverContainer>
      )}
      <Provider value={value}>{children}</Provider>
    </div>
  );
};

const Stats: FC = () => {
  const {
    wantedMentorship,
    assignedMentorship,
    finishedMentorship,
    interruptedMentorship,
    lateMentorship,
  } = useContext(MentorEditContext);

  return (
    <MentorStats
      {...{
        wantedMentorship,
        assignedMentorship,
        finishedMentorship,
        interruptedMentorship,
        lateMentorship,
      }}
    />
  );
};

const MainReferee: FC = () => {
  const { mentor } = useContext(MentorEditContext);
  return (
    <>
      <div style={{ width: 'fit-content' }}>
        <ChangeReferee mentor={mentor} />
      </div>
      <Space height={1} />
    </>
  );
};

const Navigation: FC = () => {
  const { mentor } = useContext(MentorEditContext);
  const { id } = useParams();
  const {
    assignedMentorship,
    wantedMentorship,
  }: {
    assignedMentorship: number | undefined;
    wantedMentorship: number | undefined;
  } = useSWR(`/mentorship/mentor/${id}/stats`).data || {
    assignedMentorship: undefined,
    wantedMentorship: undefined,
  };
  const baseURL = useMemo(() => {
    return `/admin/mentoring/mentors/${mentor?.id}/mentoring/`;
  }, [mentor]);
  return (
    <ProfileNavigation>
      <ProfileLink to={baseURL + 'ongoing'}>Mentorats en cours</ProfileLink>
      {typeof wantedMentorship === 'number' &&
      typeof assignedMentorship === 'number' &&
      assignedMentorship < wantedMentorship ? (
        <ProfileLink to={baseURL + 'associate'}>
          Associer de nouveaux mentorés
        </ProfileLink>
      ) : null}
      <ProfileLink to={baseURL + 'history'}>Historique</ProfileLink>
    </ProfileNavigation>
  );
};

const Usage: FC = (props) => {
  return (
    <MentorEdit {...props}>
      <Stats />
      <MainReferee />
      <Navigation />
      <MentorProfileRoutes />
    </MentorEdit>
  );
};

export default Usage;
